import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import EmployeeSidebar from '../../components/employee/EmployeeSidebar';
import LegalFooter from '../../components/shared/LegalFooter';

const fmt = (n) => parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const STATUS_BADGE = {
  paid:    { bg: '#dcfce7', color: '#16a34a' },
  pending: { bg: '#fef9c3', color: '#854d0e' },
  overdue: { bg: '#fee2e2', color: '#dc2626' },
  failed:  { bg: '#fee2e2', color: '#dc2626' },
};

export default function BillingPayouts() {
  const [tab, setTab] = useState('invoices');
  const [invoices, setInvoices] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [billingRunning, setBillingRunning] = useState(false);
  const [billingResults, setBillingResults] = useState(null);

  const runMonthlyBilling = async () => {
    if (!window.confirm('This will generate invoices and send billing emails to all eligible tenants. Continue?')) return;
    setBillingRunning(true);
    setBillingResults(null);
    try {
      const res = await api.post('/billing/admin/run-monthly');
      setBillingResults(res.data);
      toast.success(`Generated ${res.data.invoices_created} invoices, sent ${res.data.emails_sent} emails`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Billing run failed');
    } finally {
      setBillingRunning(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get('/billing/admin/invoices').catch(() => ({ data: { invoices: [] } })),
      api.get('/billing/admin/payouts').catch(() => ({ data: { payouts: [] } })),
      api.get('/billing/admin/summary').catch(() => ({ data: {} })),
    ]).then(([invRes, payRes, sumRes]) => {
      setInvoices(invRes.data.invoices || []);
      setPayouts(payRes.data.payouts || []);
      setSummary(sumRes.data);
    }).catch(() => toast.error('Could not load billing data'))
      .finally(() => setLoading(false));
  }, []);

  const filteredInvoices = invoices.filter(i => {
    const matchSearch = search === '' || `${i.tenant_name} ${i.property_name} ${i.email}`.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === '' || i.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const filteredPayouts = payouts.filter(p =>
    search === '' || `${p.landlord_name} ${p.property_name}`.toLowerCase().includes(search.toLowerCase())
  );

  const exportInvoicesCSV = () => {
    const headers = ['Tenant', 'Email', 'Property', 'Amount', 'Status', 'Due Date', 'Paid At'];
    const rows = filteredInvoices.map(i => [
      i.tenant_name, i.email, i.property_name,
      i.amount, i.status, fmtDate(i.due_date), fmtDate(i.paid_at)
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => '"' + String(v || '').replace(/"/g, '""') + '"').join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = 'invoices_' + new Date().toISOString().split('T')[0] + '.csv';
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f8f9fa' }}>
      <EmployeeSidebar />
      <div style={{ flex: 1, padding: '2rem', maxWidth: 1100 }}>
        <h1 style={{ fontSize: '1.8rem', fontWeight: 700, marginBottom: 4 }}>Billing & Payouts</h1>
        <p style={{ color: '#64748b', fontSize: 14, marginBottom: 20 }}>Platform-wide invoice and payout management.</p>

        {/* Summary cards */}
        {summary && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
            {[
              { label: 'Revenue MTD', value: '$' + fmt(summary.revenue_mtd), color: '#16a34a' },
              { label: 'Pending Invoices', value: summary.pending_count || 0, color: '#f59e0b' },
              { label: 'Overdue Invoices', value: summary.overdue_count || 0, color: '#dc2626' },
              { label: 'Pending Payouts', value: '$' + fmt(summary.pending_payouts), color: '#2563eb' },
            ].map((s, i) => (
              <div key={i} style={{ background: 'white', borderRadius: 12, padding: '1.2rem 1.5rem', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94a3b8', marginBottom: 6 }}>{s.label}</div>
                <div style={{ fontSize: '1.6rem', fontWeight: 700, color: s.color }}>{s.value}</div>
              </div>
            ))}
          </div>
        )}

        {/* Monthly billing */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <button onClick={runMonthlyBilling} disabled={billingRunning}
            style={{ background: '#2563eb', color: 'white', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: billingRunning ? 0.6 : 1 }}>
            {billingRunning ? 'Running...' : '🧾 Run Monthly Billing'}
          </button>
          {billingResults && (
            <div style={{ fontSize: 13, color: '#16a34a', fontWeight: 600 }}>
              {billingResults.invoices_created} invoices created · {billingResults.emails_sent} emails sent
              {billingResults.skipped_opted_out > 0 && ` · ${billingResults.skipped_opted_out} opted out`}
              {billingResults.skipped_existing > 0 && ` · ${billingResults.skipped_existing} already billed`}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid #e2e8f0', paddingBottom: 0 }}>
          {[{ id: 'invoices', label: '🧾 Invoices' }, { id: 'payouts', label: '💰 Payouts' }].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              background: 'none', border: 'none', padding: '10px 20px', cursor: 'pointer',
              fontSize: 14, fontWeight: 600,
              color: tab === t.id ? '#2563eb' : '#64748b',
              borderBottom: tab === t.id ? '2px solid #2563eb' : '2px solid transparent',
              marginBottom: -1,
            }}>{t.label}</button>
          ))}
        </div>

        {/* Filter row */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          <input style={{ width: 280, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14 }}
            placeholder={tab === 'invoices' ? 'Search tenant or property...' : 'Search landlord or property...'}
            value={search} onChange={e => setSearch(e.target.value)} />
          {tab === 'invoices' && (
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, background: 'white' }}>
              <option value="">All statuses</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="overdue">Overdue</option>
              <option value="failed">Failed</option>
            </select>
          )}
          {tab === 'invoices' && (
            <button onClick={exportInvoicesCSV} style={{ marginLeft: 'auto', background: '#16a34a', color: 'white', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              ⬇ Export CSV
            </button>
          )}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Loading...</div>
        ) : tab === 'invoices' ? (
          <div style={{ background: 'white', borderRadius: 14, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #e2e8f0', fontWeight: 600, fontSize: 14 }}>
              Invoices ({filteredInvoices.length})
            </div>
            {filteredInvoices.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No invoices found.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      {['Tenant', 'Property', 'Amount', 'Status', 'Due Date', 'Paid At'].map(h => (
                        <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInvoices.map((inv, i) => {
                      const badge = STATUS_BADGE[inv.status] || { bg: '#f1f5f9', color: '#64748b' };
                      return (
                        <tr key={inv.id} style={{ borderTop: '1px solid #f1f5f9', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                          <td style={{ padding: '12px 16px' }}>
                            <div style={{ fontWeight: 600 }}>{inv.tenant_name}</div>
                            <div style={{ fontSize: 11, color: '#94a3b8' }}>{inv.email}</div>
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: 12, color: '#64748b' }}>{inv.property_name}</td>
                          <td style={{ padding: '12px 16px', fontWeight: 700 }}>${fmt(inv.amount)}</td>
                          <td style={{ padding: '12px 16px' }}>
                            <span style={{ background: badge.bg, color: badge.color, borderRadius: 100, padding: '3px 10px', fontSize: 11, fontWeight: 600 }}>{inv.status}</span>
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: 12 }}>{fmtDate(inv.due_date)}</td>
                          <td style={{ padding: '12px 16px', fontSize: 12, color: inv.paid_at ? '#16a34a' : '#94a3b8' }}>{fmtDate(inv.paid_at)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          <div style={{ background: 'white', borderRadius: 14, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #e2e8f0', fontWeight: 600, fontSize: 14 }}>
              Payouts ({filteredPayouts.length})
            </div>
            {filteredPayouts.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No payouts found.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {['Landlord', 'Amount', 'Status', 'Period', 'Paid At'].map(h => (
                      <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredPayouts.map((p, i) => {
                    const badge = STATUS_BADGE[p.status] || { bg: '#f1f5f9', color: '#64748b' };
                    return (
                      <tr key={p.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '12px 16px', fontWeight: 600 }}>{p.landlord_name}</td>
                        <td style={{ padding: '12px 16px', fontWeight: 700 }}>${fmt(p.amount)}</td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{ background: badge.bg, color: badge.color, borderRadius: 100, padding: '3px 10px', fontSize: 11, fontWeight: 600 }}>{p.status}</span>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: 12, color: '#64748b' }}>{fmtDate(p.period_start)} – {fmtDate(p.period_end)}</td>
                        <td style={{ padding: '12px 16px', fontSize: 12, color: p.paid_at ? '#16a34a' : '#94a3b8' }}>{fmtDate(p.paid_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
        <LegalFooter />
      </div>
    </div>
  );
}
